import { useApolloClient } from '@apollo/client/react';
import {
  Dashboard,
  PublicOutlined,
  Rule,
  Style,
  ToggleOn,
  Work,
} from '@mui/icons-material';
import Business from '@mui/icons-material/Business';
import Chat from '@mui/icons-material/Chat';
import Email from '@mui/icons-material/Email';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import Groups from '@mui/icons-material/Groups';
import History from '@mui/icons-material/History';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Person from '@mui/icons-material/Person';
import Place from '@mui/icons-material/Place';
import SportsIcon from '@mui/icons-material/SportsMartialArts';
import * as React from 'react';
import { Admin, CustomRoutes, Resource } from 'react-admin';
// Upgrading to latest RA causes a funny issue where we need to have ONLY react-router-dom in dependencies but import Route from react-router
import { Route } from 'react-router';
import buildQuery, { introspection } from '../../admin/buildQuery';
import buildGraphQLProvider from '../../admin/dataProvider/buildGraphQLProvider';
import ExosAuthProvider from '../../admin/ExosAuthProvider';
import {
  canWrite as userCanWrite,
  isAdmin as userIsAdmin,
  isExerciseEditor as userIsExerciseEditor,
  isTestDataCreator as userIsTestDataCreator,
} from '../../admin/permissions';
import { SplitFlags, useFeatureFlags } from '../../hooks/useFeatureFlags';
import useLogout from '../../hooks/useLogout';
import theme from '../../theme';
import { EnvLevel, getEnvLevel } from '../../utils/getEnvLevel';
import { AuditLogList } from './AuditLog';
import {
  ChallengeCreate,
  ChallengeEdit,
  ChallengeShow,
  ChallengesList,
} from './Challenges';
import { ChatRoomCreate, ChatRoomEdit, ChatRoomList } from './ChatRoom';
import { ClientCreate, ClientEdit, ClientList, ClientShow } from './Client';
import { CohortCreate, CohortEdit, CohortList, CohortShow } from './Cohort';
import { ContentList, ContentShow } from './Content';
import { CustomLayout } from './CustomMenu';
import { DomainCreate, DomainEdit, DomainList, DomainShow } from './Domain';
import {
  EligibleEmailAddressCreate,
  EligibleEmailAddressEdit,
  EligibleEmailAddressList,
  EligibleEmailAddressShow,
} from './EligibleEmailAddress';
import { EmailDebugger } from './EmailDebugger';
import {
  ExerciseBaseCreate,
  ExerciseBaseEdit,
  ExerciseBaseList,
  ExerciseBaseShow,
  ExerciseCreate,
  ExerciseEdit,
  ExerciseList,
  ExerciseShow,
  ExerciseTypeCategoryCreate,
  ExerciseTypeCategoryEdit,
  ExerciseTypeCategoryGroupCreate,
  ExerciseTypeCategoryGroupEdit,
  ExerciseTypeCategoryGroupList,
  ExerciseTypeCategoryGroupShow,
  ExerciseTypeCategoryList,
  ExerciseTypeCategoryShow,
  ExerciseTypeCreate,
  ExerciseTypeEdit,
  ExerciseTypeList,
  ExerciseTypeShow,
} from './Exercise/index';
import {
  FeatureCreate,
  FeatureEdit,
  FeatureList,
  FeatureShow,
} from './Feature';
import { FeatureFlagState } from './FeatureFlageState';
import {
  FeatureGrantCreate,
  FeatureGrantEdit,
  FeatureGrantList,
  FeatureGrantShow,
} from './FeatureGrant';
import FeatureGrantCleanup from './FeatureGrantCleanup';
import { GenreCreate, GenreEdit, GenreList, GenreShow } from './Genre';
import LaunchManager from './LaunchManager';
import LaunchPage from './LaunchManager/LaunchPage';
import NewClientPage from './LaunchManager/NewClientPage';
import {
  LocationCreate,
  LocationEdit,
  LocationList,
  LocationShow,
} from './Location';
import {
  MboContractMappingCreate,
  MboContractMappingEdit,
  MboContractMappingList,
  MboContractMappingShow,
} from './MboContractMapping';
import {
  NotificationOptionCreate,
  NotificationOptionEdit,
  NotificationOptionList,
  NotificationOptionShow,
} from './NotificationOption';
import { OfficeCreate, OfficeEdit, OfficeList, OfficeShow } from './Office';
import { StudioCreate, StudioEdit, StudioList, StudioShow } from './Studio';
import { SystemPreferences } from './SystemPreferences';
import { TestData } from './TestData';
import {
  TrainingPlanTemplateEdit,
  TrainingPlanTemplateIcon,
  TrainingPlanTemplateList,
  TrainingPlanTemplateShow,
} from './TrainingPlanTemplate';
import { TrainingPlanTemplateCreate } from './TrainingPlanTemplateCreate';
import { UserCreate, UserEdit, UserList, UserShow } from './User';

const AdminPage: React.FC = () => {
  const client = useApolloClient();
  const logout = useLogout();
  const { flags } = useFeatureFlags();
  const enableTestDataService = flags[SplitFlags.SPA_TEST_DATA_SERVICE];
  const enableFeatureManagement = flags[SplitFlags.SPA_FEATURE_MANAGEMENT];
  const enableLaunchManager = flags[SplitFlags.SPA_LAUNCH_MANAGER];

  const [authProvider] = React.useState(
    () => new ExosAuthProvider(client, logout),
  );

  const dataProvider = buildGraphQLProvider({
    buildQuery,
    client,
    introspection,
  });

  if (!dataProvider) {
    return <div>Loading</div>;
  }

  return (
    <Admin
      basename="/app"
      theme={theme}
      dataProvider={dataProvider}
      authProvider={authProvider}
      loginPage={false}
      layout={CustomLayout}
    >
      {(permissions) => {
        const isAdmin = userIsAdmin(permissions);
        const canWrite = userCanWrite(permissions);
        const canManageExercises =
          canWrite || userIsExerciseEditor(permissions);
        const canAccessExerciseRoutes =
          isAdmin || userIsExerciseEditor(permissions);
        const canAccessTestData = isAdmin || userIsTestDataCreator(permissions);
        const showTestDataRoute =
          (getEnvLevel() === EnvLevel.DEV ||
            getEnvLevel() === EnvLevel.TESTING) &&
          enableTestDataService;
        return (
          <>
            {isAdmin && (
              <>
                {/* C L I E N T */}
                <Resource
                  name="Client"
                  options={{ label: 'Client' }}
                  list={ClientList}
                  create={canWrite ? ClientCreate : undefined}
                  edit={canWrite ? ClientEdit : undefined}
                  show={ClientShow}
                  icon={Business}
                  recordRepresentation={(record) => `#${record.id}`}
                />
                <Resource
                  name="Studio"
                  list={StudioList}
                  show={StudioShow}
                  edit={canWrite ? StudioEdit : undefined}
                  create={canWrite ? StudioCreate : undefined}
                  icon={FitnessCenter}
                />
                <Resource
                  name="MboContractMapping"
                  options={{ label: 'MBO Contract Mapping' }}
                  list={MboContractMappingList}
                  show={MboContractMappingShow}
                  edit={canWrite ? MboContractMappingEdit : undefined}
                  create={canWrite ? MboContractMappingCreate : undefined}
                />
                <Resource
                  name="CoachLocation"
                  list={LocationList}
                  show={LocationShow}
                  edit={canWrite ? LocationEdit : undefined}
                  create={canWrite ? LocationCreate : undefined}
                  options={{ label: 'Location' }}
                  icon={Place}
                />
                <Resource
                  name="Office"
                  list={OfficeList}
                  show={OfficeShow}
                  edit={canWrite ? OfficeEdit : undefined}
                  create={canWrite ? OfficeCreate : undefined}
                  icon={Work}
                />
                <Resource
                  name="Domain"
                  list={DomainList}
                  edit={canWrite ? DomainEdit : undefined}
                  show={DomainShow}
                  create={canWrite ? DomainCreate : undefined}
                  icon={PublicOutlined}
                />
                {/* U S E R */}
                <Resource
                  name="User"
                  options={{ label: 'User' }}
                  list={UserList}
                  create={canWrite ? UserCreate : undefined}
                  edit={canWrite ? UserEdit : undefined}
                  show={UserShow}
                  icon={Person}
                  recordRepresentation={(record) =>
                    `${record.firstName} ${record.lastName}`
                  }
                />
                <Resource
                  name="EligibleEmailAddress"
                  options={{ label: 'Eligible Email' }}
                  list={EligibleEmailAddressList}
                  show={EligibleEmailAddressShow}
                  edit={canWrite ? EligibleEmailAddressEdit : undefined}
                  create={canWrite ? EligibleEmailAddressCreate : undefined}
                  icon={Email}
                />
                <Resource
                  name="Cohort"
                  options={{ label: 'HPEC Cohort' }}
                  list={CohortList}
                  show={CohortShow}
                  edit={canWrite ? CohortEdit : undefined}
                  create={canWrite ? CohortCreate : undefined}
                  icon={Groups}
                  recordRepresentation="name"
                />
                <Resource
                  name="ChatRoom"
                  options={{ label: 'Chat Room' }}
                  list={ChatRoomList}
                  create={canWrite ? ChatRoomCreate : undefined}
                  edit={canWrite ? ChatRoomEdit : undefined}
                  icon={Chat}
                />

                {/* C O N T E N T */}
                <Resource
                  name="Content"
                  list={ContentList}
                  show={ContentShow}
                  icon={Dashboard}
                />
                <Resource
                  name="Genre"
                  list={GenreList}
                  show={GenreShow}
                  edit={canWrite ? GenreEdit : undefined}
                  create={canWrite ? GenreCreate : undefined}
                  icon={Style}
                  options={{ label: 'Genre' }}
                />
                <Resource
                  name="Challenge"
                  list={ChallengesList}
                  show={ChallengeShow}
                  edit={canWrite ? ChallengeEdit : undefined}
                  create={canWrite ? ChallengeCreate : undefined}
                  icon={EmojiEvents}
                />
                <Resource
                  name="NotificationOption"
                  options={{ label: 'Notification Options' }}
                  list={NotificationOptionList}
                  show={NotificationOptionShow}
                  edit={canWrite ? NotificationOptionEdit : undefined}
                  create={canWrite ? NotificationOptionCreate : undefined}
                  icon={NotificationsIcon}
                />
                {enableFeatureManagement && (
                  <Resource
                    name="Feature"
                    options={{ label: 'Feature' }}
                    list={FeatureList}
                    show={FeatureShow}
                    edit={canWrite ? FeatureEdit : undefined}
                    create={canWrite ? FeatureCreate : undefined}
                    icon={ToggleOn}
                  />
                )}
                {enableFeatureManagement && (
                  <Resource
                    name="FeatureGrant"
                    options={{ label: 'Feature Grant' }}
                    list={FeatureGrantList}
                    show={FeatureGrantShow}
                    edit={canWrite ? FeatureGrantEdit : undefined}
                    create={canWrite ? FeatureGrantCreate : undefined}
                    icon={Rule}
                  />
                )}
                <CustomRoutes>
                  {enableFeatureManagement && (
                    <Route
                      path="/featureGrantCleanup"
                      element={<FeatureGrantCleanup />}
                    />
                  )}
                  {enableLaunchManager && (
                    <Route path="/launchManager" element={<LaunchManager />} />
                  )}
                  {enableLaunchManager && (
                    <Route
                      path="/launchManager/new"
                      element={<NewClientPage />}
                    />
                  )}
                  {enableLaunchManager && (
                    <Route path="/launchManager/:id" element={<LaunchPage />} />
                  )}
                  <Route path="/emailDebugger" element={<EmailDebugger />} />
                  <Route
                    path="/FeatureFlagState"
                    element={<FeatureFlagState />}
                  />
                  <Route
                    path="/systemPreferences"
                    element={<SystemPreferences />}
                  />
                </CustomRoutes>
              </>
            )}
            {canAccessExerciseRoutes && (
              <>
                <Resource
                  options={{ label: 'Movement' }}
                  name="Exercise"
                  list={ExerciseList}
                  create={canManageExercises ? ExerciseCreate : undefined}
                  edit={canManageExercises ? ExerciseEdit : undefined}
                  show={ExerciseShow}
                  icon={SportsIcon}
                />
                <Resource
                  name="ExerciseTypeCategoryGroup"
                  options={{ label: 'MTC Group' }}
                  list={() => (
                    <ExerciseTypeCategoryGroupList
                      disableSyncWithLocation={false}
                    />
                  )}
                  create={
                    canManageExercises
                      ? ExerciseTypeCategoryGroupCreate
                      : undefined
                  }
                  edit={
                    canManageExercises
                      ? ExerciseTypeCategoryGroupEdit
                      : undefined
                  }
                  show={ExerciseTypeCategoryGroupShow}
                />
                <Resource
                  name="ExerciseTypeCategory"
                  options={{ label: 'MTC' }}
                  list={() => (
                    <ExerciseTypeCategoryList disableSyncWithLocation={false} />
                  )}
                  create={
                    canManageExercises ? ExerciseTypeCategoryCreate : undefined
                  }
                  edit={
                    canManageExercises ? ExerciseTypeCategoryEdit : undefined
                  }
                  show={ExerciseTypeCategoryShow}
                />
                <Resource
                  name="ExerciseType"
                  options={{ label: 'Movement Type' }}
                  list={() => (
                    <ExerciseTypeList disableSyncWithLocation={false} />
                  )}
                  create={canManageExercises ? ExerciseTypeCreate : undefined}
                  edit={canManageExercises ? ExerciseTypeEdit : undefined}
                  show={ExerciseTypeShow}
                />
                <Resource
                  name="ExerciseBase"
                  options={{ label: 'Base Movement' }}
                  list={() => (
                    <ExerciseBaseList disableSyncWithLocation={false} />
                  )}
                  create={canManageExercises ? ExerciseBaseCreate : undefined}
                  edit={canManageExercises ? ExerciseBaseEdit : undefined}
                  show={ExerciseBaseShow}
                />
                <Resource
                  name="TrainingPlan"
                  options={{ label: 'Training Plan Template' }}
                  list={TrainingPlanTemplateList}
                  show={TrainingPlanTemplateShow}
                  create={
                    canManageExercises ? TrainingPlanTemplateCreate : undefined
                  }
                  edit={
                    canManageExercises ? TrainingPlanTemplateEdit : undefined
                  }
                  icon={TrainingPlanTemplateIcon}
                />
                <Resource
                  name="AuditLog"
                  options={{ label: 'Audit Logs' }}
                  list={AuditLogList}
                  icon={History}
                />
              </>
            )}
            {showTestDataRoute && canAccessTestData && (
              <CustomRoutes>
                <Route path="/testData" element={<TestData />} />
              </CustomRoutes>
            )}
          </>
        );
      }}
    </Admin>
  );
};

export default AdminPage;
